<template>
	<section class="section container priority-features" :class="`section--${shadeMode}`">
		<home-heading v-if="content.title !== null" class="section__heading priority-features__heading" :content="content.title" />
		<div class="priority-features__box">
			<nuxt-link v-for="item in features"
								 :key="item._key"
									:to="localePath(`/${item.slug}`)"
								 class="priority-feature">
				<img :src="$urlFor(item.image, 800)"
						 :alt="item.alt"
						 class="priority-feature__image"
						 loading="lazy"
						 :height="item.height"
						 :width="item.width" />
				<div class="priority-feature__panel">
					<div class="priority-feature__text">
						<h3 v-if="item.title">{{ item.title }}</h3>
						<h4 v-if="item.subheading">{{ item.subheading }}</h4>
						<p v-if="item.text">{{item.text }}</p>
					</div>
				</div>
			</nuxt-link>
		</div>
	</section>
</template>

<script setup>
import { useI18n, useLocalePath } from '#imports'
import {useHead, useRoute} from "nuxt/app"
import {computed} from "vue";
const { locale } = useI18n()
const localePath = useLocalePath()
const route = useRoute();
const props = defineProps({
	content: Object
})
let shadeMode = props.content.isDarkMode === true ? 'dark' : 'light'
const lang = 'en';

const enFeatures = computed(() => {
	return props.content.features.map(feature => ({
		image: feature?.image?.image?.asset?.url,
		height: feature?.image?.image?.asset?.metadata?.dimensions?.height,
		width: feature?.image?.image?.asset?.metadata?.dimensions?.width,
		alt: feature?.image?.alt,
		title: feature?.title?.en,
		subheading: feature?.subheading?.en,
		text: feature?.text?.en,
		slug: feature?.slug?.current
	}));
});

const jpFeatures = computed(() => {
	return props.content.features.map(feature => ({
		image: feature?.image?.image?.asset?.url,
		height: feature?.image?.image?.asset?.metadata?.dimensions?.height,
		width: feature?.image?.image?.asset?.metadata?.dimensions?.width,
		alt: feature?.image?.alt,
		title: feature?.title?.jp,
		subheading: feature?.subheading?.jp,
		text: feature?.text?.jp,
		slug: feature?.slug?.current
	}));
});

const features = computed(() => {
	return locale.value === 'en' ? enFeatures.value : jpFeatures.value;
});
</script>

<style lang="scss" scoped>
@use '@/assets/sass/variables.scss' as v;
.priority-features {
	justify-content: space-between;
	padding: 1rem;
	@include v.respond-to('small') {
		padding: 3rem;
	}

	.priority-features__box {
		display: grid;
		grid-template-columns: 100%;
		grid-column-gap: 0;
		grid-row-gap: 1rem;
		@include v.respond-to('medium') {
			grid-template-columns: repeat(3, 1fr);
			grid-column-gap: 3rem;
			grid-row-gap: 0;
		}
		.priority-feature {
			display: block;
			margin: 0;
			padding: 0;
			overflow: hidden;
			position: relative;
			height: 20rem;
			max-height: 50vh;
			@include v.respond-to('medium') {
				height: 50rem;
				max-height: 70vh;
			}
			&__panel {
				display: block;
				text-decoration: none;
				color: v.$white;
				height: 100%;
				left: 0;
				margin: 0;
				padding: 0;
				position: absolute;
				top: 0;
				width: 100%;
				z-index: 100;
			}
			&__text {
				display: flex;
				flex-direction: column;
				align-items: flex-start;
				justify-content: flex-start;
				bottom: 0;
				width: 75%;
				padding: 0.25rem;
				position: absolute;
				left: 0;
				z-index: 100;
				@include v.respond-to('medium') {
					padding: 1rem;
				}
			}

			h3 {
				background-color: v.$transparentBg--strong;
				color: v.$white;
				margin: 0 0 0.25rem 0;
				padding: 0.5rem;
				@include v.respond-to('medium') {
					margin: 0 0 0.5rem 0;
				}
			}

			h4 {
				background-color: v.$transparentBg--strong;
				color: v.$white;
				margin: 0 0 0.25rem 0;
				padding: 0.5rem;
				@include v.respond-to('medium') {
					margin: 0 0 0.5rem 0;
				}
			}

			p {
				background-color: v.$transparentBg--strong;
				color: v.$white;
				margin: 0;
				padding: 0.5rem;
			}

			&__image {
				height: 100%;
				max-height: 50rem;
				object-fit: cover;
				//@include v.respond-to('medium') {
				//	width: auto;
				//}
			}

			&:hover {
				cursor: pointer;
				.priority-feature__panel {
					background-color: rgba(0,174,187,0.85);
					transition: all 0.3s;
				}
				.priority-feature__text h3, .priority-feature__text h4, .priority-feature__text p {
					background-color: rgba(0,0,0,0);
					transition: all 0.2s;
				}
			}
		}
	}
}
</style>
